import React, { useEffect, useRef, useState } from 'react';
import NavAppbar from '../nav-top/NavAppbar';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import PageTitle from '../page-title/PageTitle';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Paper, TextField, useMediaQuery } from '@material-ui/core';
import ProjectRequestForm from './ProjectRequestForm';
import ProjectCloseoutForm from './ProjectCloseoutForm';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';
import ProjectProgressReportsTable from './ProjectProgressReportsTable';
import { getProjectLifecyleFormsPermission } from './utils';
import Typography from '@material-ui/core/Typography';
import { getCurrentSecurityGroup } from '../../features/security/selectors';
import { getUserState } from '../../features/user/selectors';
import { useParams } from 'react-router';
import EmailUsersDialog from '../design/EmailUsersDialog';
import Button from '@material-ui/core/Button';
import { faCopy } from '@fortawesome/free-regular-svg-icons/faCopy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getProjectByKey, getProjectPublicLink, patchProject } from '../../models/api/project';
import {
  GetPublicProjectResponse,
  ProjectLifecycleFormType,
  ProjectUserPublicKeyAccessType,
} from '../../api-client/autogenerated';
import FullscreenLoader from '../loader/FullscreenLoader';
import ProjectLifecycleNotificationGroup from './components/ProjectLifecycleNotificationGroup';
import { Edit, MailOutline, Print, Save } from '@material-ui/icons';
import {
  deleteA3,
  deleteProjectCloseoutForm,
  deleteProjectRequestForm,
  sendProjectLifecycleNotificationGroupEmails,
} from '../../models/api/project-lifecycle';
import { addSnackbar } from '../../features/snackbar/actions';
import { useHistory } from 'react-router-dom';
import { createQRCode, formatDate, setPublicPageRedirect } from '../../scripts/utils';
import DeleteIcon from '@material-ui/icons/Delete';
import { useReactToPrint } from 'react-to-print';
import IconButton from '@material-ui/core/IconButton';
import { emitter, Events } from '../../scripts/events';
import A3Form from './A3';
import { updateProject } from '../../features/project/actions';
import CircularLoader from '../loader/CircularLoader';

const useStyles = makeStyles<Theme, { isMobile: boolean }>(() =>
  createStyles({
    content: {
      flexGrow: 1,
      padding: 24,
      minHeight: '100vh',
      overflowX: ({ isMobile }) => (isMobile ? 'hidden' : 'visible'),
    },
    tabsRoot: {
      minHeight: 36,
      paddingTop: 4,
      '& .MuiTabs-indicator': { display: 'none' },
    },
    tabsScroller: {
      '& .MuiTab-root.Mui-selected': { zIndex: 2 },
    },
    print: {
      '@media print': {
        margin: 16,
      },
    },
    printOnly: {
      display: 'none',
      '@media print': {
        display: 'block',
      },
    },
    deleteButton: {
      backgroundColor: '#ED3F26',
      '&:hover': {
        backgroundColor: '#FF5D45',
      },
      '&:focus': {
        backgroundColor: '#FF5D45',
      },
    },
  }),
);

interface Props {
  isPublic?: boolean;
  form: ProjectLifecycleFormType;
}

const projectLifecycleLabelMap: Record<ProjectLifecycleFormType, string> = {
  [ProjectLifecycleFormType.Request]: 'Project Lifecycle Form',
  [ProjectLifecycleFormType.Closeout]: 'Project Closeout Form',
  [ProjectLifecycleFormType.Progress]: 'Project Progress Report',
  [ProjectLifecycleFormType.A3]: 'A3 Summary',
};

export default function ProjectLifecyclePage(props: Props) {
  const { isPublic, form } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const security = useSelector(getCurrentSecurityGroup);
  const user = useSelector(getUserState);
  const project = useSelector(getProjectState);

  const formRef = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery('(max-width: 800px)');
  const classes = useStyles({ isMobile });

  const { key } = useParams<{
    key?: string;
  }>();
  const params = new URLSearchParams(window.location.search);
  const reportId = params.get('reportId') || null;

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [publicLink, setPublicLink] = useState('');
  const [getALinkDialogOpen, setGetALinkDialogOpen] = useState(false);

  const [projectStorytellerName, setProjectStorytellerName] = useState(
    project?.storytellerName || '',
  );
  const [projectStorytellerCompany, setProjectStorytellerCompany] = useState(
    project?.storytellerCompanyName || '',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [publicProject, setPublicProject] = useState<GetPublicProjectResponse | null>(null);

  const print = useReactToPrint({
    onBeforeGetContent: () => {
      emitter.emit(Events.ExpandRows);
      return new Promise((resolve) => setTimeout(resolve, 500));
    },
    content: () => formRef.current,
    bodyClass: 'print-mode',
  });

  useEffect(() => {
    if (key) {
      setIsLoading(true);
      getProjectByKey(key).then((result) => {
        setPublicProject(result);
        localStorage.setItem('publicEmail', result.email);
        if (result.projectStorytellerName) {
          setProjectStorytellerName(result.projectStorytellerName);
        }
        if (result.projectStorytellerCompanyName) {
          setProjectStorytellerCompany(result.projectStorytellerCompanyName);
        }
        setIsLoading(false);
      });
    }
  }, [key]);

  useEffect(() => {
    if (
      (form === 'request' && project?.projectRequest) ||
      (form === 'closeout' && project?.projectCloseout) ||
      (form === 'a3' && project?.projectA3)
    ) {
      getLink().then(setPublicLink);
    }
  }, [form, project]);

  const editProjectStoryteller = async () => {
    if (!project) return;

    try {
      setIsSubmitting(true);
      const updatedProject = await patchProject(project.id, {
        storytellerName: projectStorytellerName,
        storytellerCompanyName: projectStorytellerCompany,
      });
      dispatch(
        updateProject({
          storytellerName: updatedProject.storytellerName,
          storytellerCompanyName: updatedProject.storytellerCompanyName,
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const { canView, canEdit, canDelete } = getProjectLifecyleFormsPermission(
    project,
    user,
    project?.subscriber,
    security,
  );

  const getSelectedForm = () => {
    switch (form) {
      case 'request':
        return (
          <ProjectRequestForm
            canEdit={canEdit && !isPublic}
            isEditing={isEditing || !project?.projectRequest}
            publicData={publicProject?.projectRequest?.data}
          />
        );
      case 'closeout':
        return (
          <ProjectCloseoutForm
            canEdit={canEdit && !isPublic}
            isEditing={isEditing || !project?.projectCloseout}
            publicData={publicProject?.projectCloseout?.data}
          />
        );
      case 'progress':
        return (
          <ProjectProgressReportsTable
            canEdit={canEdit && !isPublic}
            canDelete={canDelete && !isPublic}
            publicData={publicProject?.projectProgressReport}
            defaultReportId={reportId}
            setIsLoading={setIsLoading}
          />
        );
      case 'a3':
        return (
          <A3Form
            canEdit={canEdit && !isPublic}
            isEditing={isEditing || !project?.projectA3}
            publicProject={publicProject}
          />
        );
      default:
        return null;
    }
  };

  const shouldShowFormActions = () => {
    if (isPublic) return false;
    if (form === 'progress') return false;

    return true;
  };

  const getLink = async () => {
    if (!project) return '';

    const { link } = await getProjectPublicLink(
      project.id,
      form === 'request'
        ? ProjectUserPublicKeyAccessType.Request
        : form === 'closeout'
        ? ProjectUserPublicKeyAccessType.Closeout
        : ProjectUserPublicKeyAccessType.A3,
    );

    return link;
  };

  const sendEmails = async () => {
    if (!project) return;

    const count = await sendProjectLifecycleNotificationGroupEmails(project.id, {
      formType: form,
    });

    dispatch(
      addSnackbar({ id: Date.now(), message: `${count} emails sent!`, severity: 'success' }),
    );
  };

  const handleResetForm = async () => {
    if (!project) return;

    const proceed = window.confirm(
      'Are you sure you want to reset this form? All of the data will be lost.',
    );
    if (!proceed) return;

    setIsLoading(true);

    try {
      if (form === 'request') {
        await deleteProjectRequestForm(project.id);
      } else if (form === 'a3') {
        await deleteA3(project.id);
      } else {
        await deleteProjectCloseoutForm(project.id);
      }

      window.location.reload();
    } catch {
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    if (publicProject) {
      return `${publicProject.projectName} ${
        publicProject?.projectRequest
          ? 'Project Lifecycle Form'
          : publicProject?.projectCloseout
          ? 'Project Closeout Form'
          : 'Project Progress Report'
      }`;
    }

    return `${project?.name} ${projectLifecycleLabelMap[form]}`;
  };

  const getPathFromForm = () => {
    switch (form) {
      case ProjectLifecycleFormType.Request:
        return 'project-lifecycle';
      case ProjectLifecycleFormType.Closeout:
        return 'project-closeout';
      case ProjectLifecycleFormType.Progress:
        return 'project-progress-reports';
      case ProjectLifecycleFormType.A3:
        return 'a3';
    }
  };

  const getUpdatedOnFromForm = () => {
    switch (form) {
      case ProjectLifecycleFormType.Request:
        return project?.projectRequest?.updatedOn || publicProject?.projectRequest?.updatedOn;
      case ProjectLifecycleFormType.Closeout:
        return project?.projectCloseout?.updatedOn || publicProject?.projectCloseout?.updatedOn;
      case ProjectLifecycleFormType.Progress:
        return null;
      case ProjectLifecycleFormType.A3:
        return project?.projectA3?.updatedOn || publicProject?.projectA3?.updatedOn;
    }
  };

  const isDataForForm = () => {
    switch (form) {
      case ProjectLifecycleFormType.Request:
        return !!project?.projectRequest;
      case ProjectLifecycleFormType.Closeout:
        return !!project?.projectCloseout;
      case ProjectLifecycleFormType.Progress:
        return false;
      case ProjectLifecycleFormType.A3:
        return !!project?.projectA3;
    }
  };

  const updatedOn = getUpdatedOnFromForm();

  const getContent = () => {
    if (!isPublic && !canView) {
      return <Typography variant="h6">You do not have permission to view this page</Typography>;
    }

    return (
      <>
        <Paper
          style={{
            padding: 16,
            paddingBottom:
              isEditing ||
              (form === 'request' && !project?.projectRequest) ||
              (form === 'closeout' && !project?.projectCloseout) ||
              form === 'progress'
                ? 0
                : 16,
            boxShadow: '0px 5px 15px rgb(0, 0, 0, .15)',
            maxWidth: isMobile ? '100%' : 1000,
          }}
        >
          {shouldShowFormActions() ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  <FontAwesomeIcon
                    width={32}
                    height={32}
                    icon={faCopy}
                    style={{ width: 20, height: 20, color: '#FFF' }}
                  />
                }
                disabled={!publicLink}
                onClick={() => setGetALinkDialogOpen(true)}
              >
                Get A Link
              </Button>
              {canEdit ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MailOutline />}
                  onClick={sendEmails}
                  disabled={!project?.projectLifecycleNotificationGroupId || !isDataForForm()}
                >
                  Notify Group
                </Button>
              ) : null}
              <div style={{ display: 'inline-flex', flexGrow: 1 }} />
              {canEdit ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Edit />}
                  disabled={isEditing || !isDataForForm()}
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              ) : null}
              {canDelete ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DeleteIcon />}
                  disabled={!isDataForForm()}
                  onClick={handleResetForm}
                  className={classes.deleteButton}
                >
                  Reset Form
                </Button>
              ) : null}
              {form !== 'a3' ? (
                <IconButton onClick={print} style={{ padding: 0 }}>
                  <Print />
                </IconButton>
              ) : null}
            </div>
          ) : null}

          <MuiPickersUtilsProvider utils={DayjsUtils}>
            <div ref={formRef} className={classes.print}>
              <Typography
                className={classes.printOnly}
                style={{
                  fontSize: '34px',
                  lineHeight: '40px',
                  fontWeight: 600,
                  letterSpacing: '-0.6px',
                  color: '#0947B9',
                  maxWidth: 'calc(100% - 120px)',
                  marginBottom: 32,
                }}
              >
                {project?.name} {projectLifecycleLabelMap[form]}
              </Typography>
              <div
                ref={(ref) => {
                  if (ref)
                    createQRCode({
                      qrcodeRef: ref,
                      link: publicLink,
                      width: 112,
                      height: 112,
                      drawer: 'svg',
                    });
                }}
                className={classes.printOnly}
                style={{ position: 'absolute', top: 8, right: 8 }}
              />
              {getSelectedForm()}
            </div>
          </MuiPickersUtilsProvider>
        </Paper>
      </>
    );
  };

  if (!canView && !isPublic) {
    return (
      <main className={classes.content}>
        <NavAppbar />
        <Typography variant="h6">You do not have permission to view this page</Typography>
      </main>
    );
  }

  return (
    <main className={classes.content}>
      <NavAppbar />
      {isLoading ? (
        <FullscreenLoader />
      ) : (
        <>
          <PageTitle title={getTitle()} />
          {isPublic ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setPublicPageRedirect(
                  `/main/projects/${publicProject?.projectId}/${getPathFromForm()}/${
                    publicProject?.projectProgressReport
                      ? `?reportId=${publicProject.projectProgressReport.id}`
                      : ''
                  }`,
                );
                history.push('/main/login');
              }}
            >
              Login
            </Button>
          ) : null}
          {!isPublic && canEdit ? <ProjectLifecycleNotificationGroup /> : null}
          <div style={{ height: 32 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}>
            <Typography style={{ fontWeight: 600 }}>Project Storyteller:</Typography>
            <TextField
              size="small"
              variant="outlined"
              label="Name"
              disabled={!canEdit}
              value={projectStorytellerName}
              onChange={(e) => setProjectStorytellerName(e.target.value)}
            />
            <TextField
              size="small"
              variant="outlined"
              label="Company"
              disabled={!canEdit}
              value={projectStorytellerCompany}
              onChange={(e) => setProjectStorytellerCompany(e.target.value)}
            />
            {canEdit ? (
              <>
                {!isSubmitting ? (
                  <Button
                    color="primary"
                    variant="contained"
                    startIcon={<Save />}
                    onClick={editProjectStoryteller}
                  >
                    Save
                  </Button>
                ) : (
                  <CircularLoader size={25} style={{ width: 110 }} />
                )}
              </>
            ) : null}
          </div>
          {form !== ProjectLifecycleFormType.Progress ? (
            <Typography style={{ marginBottom: 32, fontWeight: 600 }}>
              Last Updated: {updatedOn ? formatDate(updatedOn, true) : 'N/A'}
            </Typography>
          ) : null}
          {getContent()}
        </>
      )}
      <EmailUsersDialog
        dialogOpen={getALinkDialogOpen}
        closeDialog={() => setGetALinkDialogOpen(false)}
        getLink={() => new Promise<string>((resolve) => resolve(publicLink))}
      />
    </main>
  );
}
